import React, { useEffect, useState } from 'react';
import { X, CheckSquare, FileText, GraduationCap, ListChecks, Clock, AlertCircle } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const TaskNotificationPopup = () => {
    const { popupNotifications, dismissNotification, markAsRead } = useNotifications();
    const [currentNotification, setCurrentNotification] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (popupNotifications.length > 0 && !currentNotification) {
            // Show the first unread notification
            setCurrentNotification(popupNotifications[0]);
            setIsVisible(true);
        }
    }, [popupNotifications, currentNotification]);

    const getTaskStyle = (taskType) => {
        const props = { size: 20, strokeWidth: 2.2 };
        switch (taskType) {
            case 'quiz':
                return { icon: <FileText {...props} className="text-blue-600" />, bg: "bg-blue-50 border-blue-100", glow: "from-blue-200/60" };
            case 'training':
                return { icon: <GraduationCap {...props} className="text-purple-600" />, bg: "bg-purple-50 border-purple-100", glow: "from-purple-200/60" };
            case 'checklist':
                return { icon: <ListChecks {...props} className="text-emerald-600" />, bg: "bg-emerald-50 border-emerald-100", glow: "from-emerald-200/60" };
            default:
                return { icon: <CheckSquare {...props} className="text-indigo-600" />, bg: "bg-indigo-50 border-indigo-100", glow: "from-indigo-200/60" };
        }
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return null;

        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { text: 'Overdue', color: 'text-red-500', icon: AlertCircle };
        } else if (diffDays === 0) {
            return { text: 'Due Today', color: 'text-orange-500', icon: Clock };
        } else if (diffDays === 1) {
            return { text: 'Due Tomorrow', color: 'text-orange-400', icon: Clock };
        } else if (diffDays <= 7) {
            return { text: `In ${diffDays} days`, color: 'text-amber-500', icon: Clock };
        } else {
            return { text: date.toLocaleDateString(), color: 'text-slate-400', icon: Clock };
        }
    };

    const handleDismiss = async () => {
        if (currentNotification) {
            setIsVisible(false);
            await dismissNotification(currentNotification.id);

            // Show next notification after a delay
            setTimeout(() => {
                setCurrentNotification(null);
            }, 300);
        }
    };

    const handleViewTask = async () => {
        if (currentNotification) {
            await markAsRead(currentNotification.id);
            setIsVisible(false);

            // Navigate to tasks page
            navigate('/employee/tasks');

            setTimeout(() => {
                setCurrentNotification(null);
            }, 300);
        }
    };

    const handleMarkAsRead = async () => {
        if (currentNotification) {
            setIsVisible(false);
            await markAsRead(currentNotification.id);

            setTimeout(() => {
                setCurrentNotification(null);
            }, 300);
        }
    };

    if (!currentNotification || !isVisible) return null;

    const dueInfo = formatDueDate(currentNotification.due_date);
    const DueIcon = dueInfo?.icon;
    const styleInfo = getTaskStyle(currentNotification.task_type);

    return (
        <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end pointer-events-none space-y-3">
            <div
                className={`
                    pointer-events-auto w-[330px] max-w-[calc(100vw-2rem)] bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-100/80
                    transform transition-all duration-500 cubic-bezier(0.16, 1, 0.3, 1) relative overflow-hidden group
                    ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'}
                `}
                style={{
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 20px 40px -15px rgba(0,0,0,0.15)'
                }}
            >
                {/* Accent glow behind icon */}
                <div className={`absolute top-0 left-0 w-40 h-40 bg-gradient-to-br ${styleInfo.glow} to-transparent rounded-full blur-2xl -translate-x-12 -translate-y-12`} />
                <div className={`absolute top-0 right-0 w-full h-1 bg-gradient-to-r ${styleInfo.glow.replace('200/60', '500')} to-transparent opacity-20`} />

                <div className="p-4 relative z-10 w-full">
                    {/* Header */}
                    <div className="flex items-start gap-3.5">
                        {/* Icon */}
                        <div className={`w-11 h-11 rounded-[14px] flex items-center justify-center border ${styleInfo.bg} flex-shrink-0 relative shadow-sm`}>
                            {styleInfo.icon}
                            {/* Priority Indicator Dot */}
                            {currentNotification.priority && (
                                <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm
                                    ${currentNotification.priority === 'high' ? 'bg-red-500' : ''}
                                    ${currentNotification.priority === 'medium' ? 'bg-amber-500' : ''}
                                    ${currentNotification.priority === 'low' ? 'bg-blue-500' : 'bg-slate-400'}
                                `} />
                            )}
                        </div>

                        {/* Text */}
                        <div className="flex-1 pt-0.5 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-0.5">
                                <div className="min-w-0">
                                    <h3 className="font-extrabold text-slate-900 text-[14px] tracking-tight truncate leading-tight">
                                        {currentNotification.title || 'Task Pending'}
                                    </h3>
                                    <h4 className="font-medium text-slate-600 text-[13px] leading-snug mt-0.5 truncate">
                                        {currentNotification.task_title}
                                    </h4>
                                </div>
                                <button
                                    onClick={handleDismiss}
                                    className="p-1 -mt-1 -mr-1 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600 flex-shrink-0"
                                >
                                    <X size={14} strokeWidth={2.5} />
                                </button>
                            </div>

                            {currentNotification.task_description && (
                                <p className="text-[12px] text-slate-500 line-clamp-2 leading-relaxed mt-1.5 font-medium">
                                    {currentNotification.task_description}
                                </p>
                            )}

                            {/* Meta */}
                            <div className="flex items-center gap-3 mt-3">
                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider uppercase border
                                    ${currentNotification.task_type === 'quiz' ? 'bg-blue-50 text-blue-600 border-blue-100' : ''}
                                    ${currentNotification.task_type === 'training' ? 'bg-purple-50 text-purple-600 border-purple-100' : ''}
                                    ${currentNotification.task_type === 'checklist' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : ''}
                                    ${!['quiz', 'training', 'checklist'].includes(currentNotification.task_type) ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : ''}
                                `}>
                                    {currentNotification.task_category || currentNotification.task_type || 'Task'}
                                </span>
                                {dueInfo && (
                                    <div className={`flex items-center gap-1 text-[11px] font-bold tracking-wide ${dueInfo.color}`}>
                                        {DueIcon && <DueIcon size={12} strokeWidth={2.5} />}
                                        <span>{dueInfo.text}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex gap-2.5">
                        <button
                            onClick={handleViewTask}
                            className="flex-1 px-3 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-[13px] font-bold shadow-md shadow-slate-900/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2 group/btn"
                        >
                            View Details
                            <span className="group-hover/btn:translate-x-0.5 transition-transform">→</span>
                        </button>
                        <button
                            onClick={handleMarkAsRead}
                            className="px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-600 rounded-xl text-[13px] font-bold transition-all active:scale-[0.98]"
                        >
                            Later
                        </button>
                    </div>
                </div>
            </div>

            {/* Multiple notifications indicator stack */}
            {popupNotifications.length > 1 && (
                <div
                    className={`
                        pointer-events-auto bg-slate-800/95 backdrop-blur-md px-3 py-1.5 rounded-full shadow-lg border border-slate-700
                        transform transition-all duration-500 delay-100 flex items-center gap-2 mr-1
                        ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'}
                    `}
                >
                    <div className="flex gap-1">
                        {popupNotifications.slice(0, 3).map((_, i) => (
                            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-white' : 'bg-slate-500'}`} />
                        ))}
                    </div>
                    <span className="text-[10px] font-bold text-slate-300 tracking-widest uppercase">
                        {popupNotifications.length} Pending
                    </span>
                </div>
            )}
        </div>
    );
};

export default TaskNotificationPopup;
